import React, { Component } from 'react';
import styled from 'styled-components';
import { isEqual, get } from 'lodash';
import {
  makeExternalUrl,
  trackAnalytics,
  toSlug,
  resolveAccessibleLabel,
} from '../../../../lib/utils';
import HeartIcon from '../../../svgs/HeartIcon';
import MilesOffer from '../shared/MilesOffer';
import PinterestAnalyticsTracker from '../shared/PinterestAnalyticsTracker';
import {
  DATA_TRACK_TYPE_CTA,
  DATA_TRACK_CLICK_SHOP,
  DOM_SHOP_CARD_INFO,
} from '../../../../lib/constants';
import { saveShop, removeShopById } from '../../../../lib/user';
import MediaQueryHelper from '../../../templates/MediaQueryHelper';
import DateDisplay from '../DateDisplay/DateDisplay';
import PromoBadge from '../PromoBadge/PromoBadge';
import Button from '../Button/Button';
import ShopNowButton from '../Button/ShopNowButton';

const ShopInfoCardDiv = styled.div`
  ${({ theme }) => theme['elevation-shadow']['1']}
  background-color: ${p => p.theme.c.bg.primary};
  width: ${p => p.theme.unit(33)};
  height: ${p => p.theme.unit(54)};

  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  padding-top: ${p => p.theme.unit(3)};
  padding-bottom: ${p => p.theme.unit(4.25)};
  padding-right: ${p => p.theme.unit(3.5)};
  padding-left: ${p => p.theme.unit(3.5)};
  position: relative;
  margin-top: ${p => p.theme.unit(-34)};

  & .buttonWrapper {
    width: 100%;
  }

  & .button-text-wrap {
    padding-left: 14px;
    padding-right: 14px;

    line-height: 1;
  }
`;

const ShopInfoPromoBadge = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: left;
  width: 100%;
  padding: ${p => p.theme.unit(1)};
`;

const GutterTopDiv = styled.div`
  padding-top: ${p => p.theme.unit(p.gutter)};
  width: 100%;
`;

const CardImgContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  text-decoration: none;
  width: ${p => p.theme.unit(21)};
  height: ${p => p.theme.unit(12)};
`;

const ShopLogoImg = styled.img`
  max-width: ${p => p.theme.unit(16)};
  max-height: ${p => p.theme.unit(8)};
`;

const ShopNameSpan = styled.span`
  ${({ theme }) => theme.typographyRebrand.heading.lg}
  color: ${p => p.theme.c.text.primary};
`;

/** Mobile Styles **/
const MobileShopInfoCardDiv = styled(ShopInfoCardDiv)`
  width: 100%;
  max-width: ${p => p.theme.unit(50)};
  height: ${p => p.theme.unit(48)};
  margin-top: ${p => p.theme.unit(-3)};
`;

const MobileCardImgContainer = styled(CardImgContainer)`
  width: ${p => p.theme.unit(11)};
  height: ${p => p.theme.unit(6)};
`;

const MobileShopLogoImg = styled(ShopLogoImg)`
  max-width: ${p => p.theme.unit(8)};
  max-height: ${p => p.theme.unit(4.5)};
`;

const MobileShopNameSpan = styled(ShopNameSpan)``;

const DateContainer = styled.div``;

const emptyCallback = () => {};

class ShopInfoCard extends Component {

  constructor(props) {
    super(props);

    this.state = { isHovering: false };
    this.favouriteButton;
  }

  shouldComponentUpdate(nextProps, nextState) {
    // Only update if favourite status changes or mouse is hovering
    // TODO look into alternative way of doing the 'hover' state
    if (!isEqual(get(nextProps, 'userShopsData'), get(this.props, 'userShopsData')) ||
        !isEqual(get(nextState, 'isHovering'), get(this.state, 'isHovering'))) {
      return true;
    }
  }

  componentDidUpdate() {
    // After updating, set focus to favourite button if applicable
    this.props.pageStateModifiers &&
      this.props.pageStateModifiers.setElementFocus &&
      this.props.pageStateModifiers.setElementFocus(this.props.pageContext, this.props.id, this.favouriteButton);
  }

  render() {
    const config = this.props.config || {};

    const {
      id,
      displayName,
      slug,
      active,
      affiliateUrl,
      baseMiles,
      amountPerBaseMiles,
      mediumImageUrl,
      mediumImageAltText,
      multiplier = {},
      pageStateModifiers = {},
      locale,
      dataLayerId,
      dataContextContainer,
      userShopsData = [],
      broker,
      isMobile = false,
      pageContext,
      linkTextPrepositions,
      siteWideMultiplier
    } = this.props;

    const singularMilesText = config.singularMilesText || '';
    const pluralMilesText = config.pluralMilesText || '';
    const singularFlatRateMilesText = config.singularFlatRateMilesText || '';
    const pluralFlatRateMilesText = config.pluralFlatRateMilesText || '';
    const primaryText = config.primaryText || '';
    const primaryButtonTagSet = config.primaryButtonTagSet || {};
    const favouriteButtonText = config.favouriteButtonText || '';
    const favouriteButtonTagSet = config.favouriteButtonTagSet || {};
    const favouritedButtonText = config.favouritedButtonText || '';
    const favouritedButtonTagSet = config.favouritedButtonTagSet || {};
    const milesBadgeText = config.milesBadgeText || '';
    const milesEndsText = config.milesEndsText || '';
    const favouriteAddLabel = config.favouriteAddLabel.replace('%s', displayName) || '';
    const favouriteRemoveLabel = config.favouriteRemoveLabel.replace('%s', displayName) || '';
    const extraMilesText =  config.extraMilesText || '';

    const {
      syncUserDataOnPage = emptyCallback,
      isUserLoggedIn = emptyCallback,
      makeAndOpenSignInModal = emptyCallback,
      setActiveFocus = emptyCallback,
    } = pageStateModifiers;

    const shopNowButtonProps = {
      // Resolve interstitial URL params
      shopSlug: slug,
      locale,
      // Track analytics params
      trackAnalytics: (redirectLink) => trackAnalytics(
        DOM_SHOP_CARD_INFO,
        toSlug(primaryText),
        redirectLink,
        primaryButtonTagSet,
        undefined,
        slug
      )
    };

    // Fav buttons
    const favouriteTrack = trackAnalytics(
      DOM_SHOP_CARD_INFO, toSlug(favouriteButtonText), '', favouriteButtonTagSet, '', slug);

    const favouritedTrack = trackAnalytics(
      DOM_SHOP_CARD_INFO, toSlug(favouritedButtonText), '', favouritedButtonTagSet, '', slug);

    // *** HANDLER ***
    const removeShopHandler = () => {
      if (isUserLoggedIn()) {
        removeShopById(id, () => { setActiveFocus(pageContext, id); syncUserDataOnPage(); });
      } else {
        makeAndOpenSignInModal();
      }
    };

    const saveShopHandler = () => {
      if (isUserLoggedIn()) {
        saveShop(id, () => { setActiveFocus(pageContext, id); syncUserDataOnPage(); });
      } else {
        makeAndOpenSignInModal();
      }
    };

    const userShopsIds = userShopsData.map(shopData => shopData.id);
    const isFavourite = userShopsIds.includes(id);
    const accessibleLabel = resolveAccessibleLabel(null, { displayName }, linkTextPrepositions, 'at1', primaryText);

    /** Desktop component **/
    const DesktopBody = () => {
      return (
        <ShopInfoCardDiv
          className="hover-container"
          data-layer-source-id={dataLayerId}
          data-context-container={dataContextContainer}>
          { multiplier && (
            <ShopInfoPromoBadge>
              <PromoBadge
                extraMilesText={extraMilesText}
                pluralFlatRateMilesText={pluralFlatRateMilesText} />
            </ShopInfoPromoBadge>
          )}
          {/* Shops logo */}
          <CardImgContainer>
            <ShopLogoImg src={makeExternalUrl(mediumImageUrl)} alt={mediumImageAltText} />
          </CardImgContainer>

          {/* Shop Name */}
          <GutterTopDiv gutter={2} />
          <ShopNameSpan>
            {displayName}
          </ShopNameSpan>

          {/* Miles */}
          <GutterTopDiv gutter={1} />
          <MilesOffer
            size="sm"
            multiplier={multiplier}
            siteWideMultiplier={siteWideMultiplier}
            baseMiles={baseMiles}
            amountPerBaseMiles={amountPerBaseMiles}
            singularMilesText={singularMilesText}
            pluralMilesText={pluralMilesText}
            singularFlatRateMilesText={singularFlatRateMilesText}
            pluralFlatRateMilesText={pluralFlatRateMilesText}
            milesBadgeText={milesBadgeText}
            locale={locale}
          />

          {multiplier || siteWideMultiplier ? (
            <div>
              <GutterTopDiv gutter={1} />
              <DateContainer>
                <DateDisplay
                  preText={milesEndsText}
                  dates={[multiplier && multiplier.endDate, siteWideMultiplier && siteWideMultiplier.endDate]}
                  locale={locale}
                />
              </DateContainer>
            </div>
          ) : null}

          {/* Shop Now button */}
          <GutterTopDiv gutter={2} />
          <PinterestAnalyticsTracker className="buttonWrapper" leadName={primaryText} shopSlug={slug}>
            <ShopNowButton
              variant="primary"
              size="medium"
              theme="light"
              fullWidth
              buttonText={primaryText}
              accessibleLabel={accessibleLabel}
              {...shopNowButtonProps}
            />
          </PinterestAnalyticsTracker>
          {/* Favourite button */}
          <GutterTopDiv gutter={2} />
          <Button
            variant="secondary"
            size="medium"
            theme="light"
            className="button-text-wrap"
            fullWidth
            renderLeftIcon={({ className }) => <HeartIcon hover={this.state.isHovering} active={isFavourite} className={className} />}
            aria-label={isFavourite ? favouriteRemoveLabel : favouriteAddLabel}
            data-track-type={DATA_TRACK_TYPE_CTA}
            data-track-click={DATA_TRACK_CLICK_SHOP}
            data-track-id={`${DOM_SHOP_CARD_INFO}_${slug}_favorite-icon-${isFavourite ? 'remove' : 'add'}`}
            id={isFavourite ? favouritedTrack.dataTrackingId : favouriteTrack.dataTrackingId}
            title={isFavourite ? favouriteRemoveLabel : favouriteAddLabel}
            onClick={isFavourite ? () => { removeShopHandler(isMobile) } : () => { saveShopHandler(isMobile) }}
            innerRef={element => this.favouriteButton = element}
            onMouseEnter={()=>{this.setState({isHovering: true})}}
            onMouseLeave={()=>{this.setState({isHovering: false})}}
            buttonText={isFavourite ? favouritedButtonText : favouriteButtonText}
          />
        </ShopInfoCardDiv>
      );
    };

    /** Mobile component **/
    const MobileBody = () => {
      return (
        <MobileShopInfoCardDiv
          data-layer-source-id={dataLayerId}
          data-context-container={dataContextContainer}>
          { multiplier && (
            <ShopInfoPromoBadge>
              <PromoBadge
                extraMilesText={extraMilesText}
                pluralFlatRateMilesText={pluralFlatRateMilesText} />
            </ShopInfoPromoBadge>
          )}
          {/* Shops logo */}
          <MobileCardImgContainer>
            <MobileShopLogoImg src={makeExternalUrl(mediumImageUrl)} alt={mediumImageAltText} />
          </MobileCardImgContainer>

          {/* Shop Name */}
          <GutterTopDiv gutter={2} />
          <MobileShopNameSpan>
            {displayName}
          </MobileShopNameSpan>

          {/* Miles */}
          <GutterTopDiv gutter={1} />
          <MilesOffer
            size="sm"
            multiplier={multiplier}
            siteWideMultiplier={siteWideMultiplier}
            baseMiles={baseMiles}
            amountPerBaseMiles={amountPerBaseMiles}
            singularMilesText={singularMilesText}
            pluralMilesText={pluralMilesText}
            singularFlatRateMilesText={singularFlatRateMilesText}
            pluralFlatRateMilesText={pluralFlatRateMilesText}
            milesBadgeText={milesBadgeText}
            locale={locale} />

          {multiplier || siteWideMultiplier ? (

            <div>
              <GutterTopDiv gutter={0.5} />
              <DateContainer>
                <DateDisplay
                  preText={milesEndsText}
                  dates={[multiplier && multiplier.endDate, siteWideMultiplier && siteWideMultiplier.endDate]}
                  locale={locale}
                />
              </DateContainer>
            </div>
          ) : null}

          {/* Shop Now button */}
          <div>
            <GutterTopDiv gutter={2} />
            <PinterestAnalyticsTracker
              leadName={primaryText}
              shopSlug={slug}
            >
              <ShopNowButton
                variant="primary"
                size="large"
                theme="light"
                fullWidth
                buttonText={primaryText}
                accessibleLabel={accessibleLabel}
                {...shopNowButtonProps}
              />
            </PinterestAnalyticsTracker>
            <GutterTopDiv gutter={2} />
            <Button
              variant="secondary"
              size="large"
              theme="light"
              className="button-text-wrap"
              fullWidth
              renderLeftIcon={({ className }) => <HeartIcon hover={this.state.isHovering} active={isFavourite} className={className} />}
              aria-label={isFavourite ? favouriteRemoveLabel : favouriteAddLabel}
              data-track-type={DATA_TRACK_TYPE_CTA}
              data-track-click={DATA_TRACK_CLICK_SHOP}
              data-track-id={`${DOM_SHOP_CARD_INFO}_${slug}_favorite-icon-${isFavourite ? 'remove' : 'add'}`}
              id={isFavourite ? favouritedTrack.dataTrackingId : favouriteTrack.dataTrackingId}
              title={isFavourite ? favouriteRemoveLabel : favouriteAddLabel}
              onClick={isFavourite ? () => { removeShopHandler(isMobile) } : () => { saveShopHandler(isMobile) }}
              innerRef={element => this.favouriteButton = element}
              onMouseEnter={()=>{this.setState({isHovering: true})}}
              onMouseLeave={()=>{this.setState({isHovering: false})}}
              buttonText={isFavourite ? favouritedButtonText : favouriteButtonText}
            />
          </div>
        </MobileShopInfoCardDiv>
      );
    };

    const renderComp = (
      <MediaQueryHelper
        desktopChildren={DesktopBody()}
        mobileChildren={MobileBody()} />);
    return renderComp;
  }


};

export default ShopInfoCard;
